import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, Home as HomeIcon, Key, FileText, Users, CheckCircle, Search } from 'lucide-react';
import { PropertyCard } from '../components/PropertyCard';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const DEFAULT_HERO_IMAGE = "https://static.prod-images.emergentagent.com/jobs/9e272307-903f-46fd-bb53-c9ce0cd00ac2/images/5927d81fa37fb38b133452d1b0f13ad45ce3428a0be5ecefa7e2d80ea9a8b576.png";

const propertyTypes = ['Appartamento', 'Villa', 'Mansarda', 'Terreno', 'Locale Commerciale', 'Ufficio', 'Box Auto', 'Posto Auto', 'Soluzione Semi-Indipendente', 'Soluzione Indipendente'];
const contractTypes = [
  { value: 'vendita', label: 'In Vendita' },
  { value: 'affitto', label: 'In Affitto' },
];
const cities = ['Caserta', 'Casagiove', 'San Nicola la Strada', 'Maddaloni', 'Marcianise', 'Capua', 'Santa Maria C.V.', 'Aversa', 'Recale'];

const services = [
  { icon: HomeIcon, title: 'Compravendita', desc: 'Assistenza completa per acquisto e vendita' },
  { icon: Key, title: 'Affitti', desc: 'Gestione locazioni residenziali e commerciali' },
  { icon: FileText, title: 'Valutazioni', desc: 'Stime professionali del valore immobiliare' },
  { icon: Users, title: 'Consulenza', desc: 'Supporto tecnico e legale personalizzato' },
];

const stats = [
  { value: '15+', label: 'Anni di Esperienza' },
  { value: '+500', label: 'Immobili Venduti' },
  { value: '98%', label: 'Recensioni Positive' },
  { value: '+1000', label: 'Famiglie Soddisfatte' },
];

function useInView(threshold = 0.15) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setInView(true); obs.disconnect(); } }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

export default function HomePage() {
  const [featuredProperties, setFeaturedProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [heroSettings, setHeroSettings] = useState(null);
  const navigate = useNavigate();

  const [searchFilters, setSearchFilters] = useState({
    city: '',
    contract_type: '',
    property_type: '',
  });

  const [statsRef, statsInView] = useInView();
  const [featuredRef, featuredInView] = useInView();
  const [servicesRef, servicesInView] = useInView();
  const [whyRef, whyInView] = useInView();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [propsRes, settingsRes] = await Promise.all([
          axios.get(`${API}/properties?featured=true&limit=6`),
          axios.get(`${API}/home-settings`),
        ]);
        setFeaturedProperties(propsRes.data);
        setHeroSettings(settingsRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleSearch = () => {
    const params = new URLSearchParams();
    if (searchFilters.city && searchFilters.city !== 'all') params.append('city', searchFilters.city);
    if (searchFilters.contract_type && searchFilters.contract_type !== 'all') params.append('contract', searchFilters.contract_type);
    if (searchFilters.property_type && searchFilters.property_type !== 'all') params.append('type', searchFilters.property_type);
    navigate(`/properties?${params.toString()}`);
  };

  const heroTitle = heroSettings?.hero_title || 'Trova la casa dei tuoi sogni';
  const heroSubtitle = heroSettings?.hero_subtitle || "Green House ti accompagna nella ricerca dell'immobile perfetto. Esperienza, professionalità e passione al servizio dei tuoi desideri.";
  const heroImageUrl = heroSettings?.hero_image_url
    ? `${API}/files/${heroSettings.hero_image_url}`
    : DEFAULT_HERO_IMAGE;

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0" style={{ transform: 'translateZ(0)' }}>
          <img src={heroImageUrl} alt="Luxury Home" className="w-full h-full object-cover scale-110" style={{ willChange: 'transform' }} />
          <div className="absolute inset-0 hero-overlay" />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 w-full">
          <div className="max-w-3xl animate-fadeIn">
            <p className="text-xs font-sans uppercase tracking-[0.2em] font-medium text-accent mb-4">
              Dal 2011 al tuo fianco
            </p>
            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl text-white tracking-tight leading-[1.1] mb-4">
              {heroTitle}
            </h1>
            <p className="font-sans text-base md:text-lg text-white/80 leading-relaxed mb-10">
              {heroSubtitle}
            </p>
          </div>

          {/* Search Bar */}
          <div
            className="bg-white rounded-xl p-4 md:p-5 shadow-2xl max-w-4xl animate-fadeIn"
            style={{ animationDelay: '0.3s' }}
            data-testid="hero-search-bar"
          >
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Select value={searchFilters.city} onValueChange={(v) => setSearchFilters({ ...searchFilters, city: v })}>
                <SelectTrigger className="h-12 bg-white border-border text-text-main" data-testid="hero-filter-city">
                  <SelectValue placeholder="Città" />
                </SelectTrigger>
                <SelectContent className="bg-white border border-border shadow-xl z-[100]">
                  <SelectItem value="all" className="text-text-main hover:bg-background-alt">Tutte le città</SelectItem>
                  {cities.map((c) => (
                    <SelectItem key={c} value={c} className="text-text-main hover:bg-background-alt">{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={searchFilters.contract_type} onValueChange={(v) => setSearchFilters({ ...searchFilters, contract_type: v })}>
                <SelectTrigger className="h-12 bg-white border-border text-text-main" data-testid="hero-filter-contract">
                  <SelectValue placeholder="Tipo Contratto" />
                </SelectTrigger>
                <SelectContent className="bg-white border border-border shadow-xl z-[100]">
                  <SelectItem value="all" className="text-text-main hover:bg-background-alt">Tutti</SelectItem>
                  {contractTypes.map((t) => (
                    <SelectItem key={t.value} value={t.value} className="text-text-main hover:bg-background-alt">{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={searchFilters.property_type} onValueChange={(v) => setSearchFilters({ ...searchFilters, property_type: v })}>
                <SelectTrigger className="h-12 bg-white border-border text-text-main" data-testid="hero-filter-type">
                  <SelectValue placeholder="Tipologia" />
                </SelectTrigger>
                <SelectContent className="bg-white border border-border shadow-xl z-[100]">
                  <SelectItem value="all" className="text-text-main hover:bg-background-alt">Tutte</SelectItem>
                  {propertyTypes.map((t) => (
                    <SelectItem key={t} value={t} className="text-text-main hover:bg-background-alt">{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button type="button" onClick={handleSearch} className="h-12 bg-primary hover:bg-primary-hover text-white col-span-2 md:col-span-1" data-testid="hero-search-submit">
                <Search size={18} className="mr-2" />
                Cerca
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section ref={statsRef} className="bg-primary py-16" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className={`text-center transition-all duration-700 ${statsInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{ transitionDelay: `${index * 150}ms` }}>
                <p className="font-serif text-4xl md:text-5xl text-white font-semibold">{stat.value}</p>
                <p className="font-sans text-sm text-white/70 mt-2">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Properties */}
      <section ref={featuredRef} className="py-24 bg-background" data-testid="featured-properties-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`text-center mb-16 transition-all duration-700 ${featuredInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <p className="text-xs font-sans uppercase tracking-[0.2em] font-medium text-primary mb-4">Selezione Esclusiva</p>
            <h2 className="font-serif text-4xl md:text-5xl text-text-main tracking-tight">Immobili in Evidenza</h2>
          </div>
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3].map((i) => (
                <div key={i} className="animate-pulse">
                  <div className="bg-gray-200 rounded-xl aspect-[4/3] mb-4" />
                  <div className="h-6 bg-gray-200 rounded w-1/3 mb-2" />
                  <div className="h-5 bg-gray-200 rounded w-2/3" />
                </div>
              ))}
            </div>
          ) : featuredProperties.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {featuredProperties.map((property, index) => (
                <PropertyCard key={property.id} property={property} index={index} />
              ))}
            </div>
          ) : (
            <p className="text-center text-text-muted font-sans">Nessun immobile in evidenza al momento.</p>
          )}
          <div className="text-center mt-12">
            <Link to="/properties" className="inline-flex items-center text-primary font-sans font-medium hover:text-primary-hover transition-colors" data-testid="view-all-properties">
              Vedi tutti gli annunci <ArrowRight size={18} className="ml-2" />
            </Link>
          </div>
        </div>
      </section>

      {/* Services */}
      <section ref={servicesRef} className="py-24 bg-background-alt" data-testid="services-preview-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`text-center mb-16 transition-all duration-700 ${servicesInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <p className="text-xs font-sans uppercase tracking-[0.2em] font-medium text-primary mb-4">Cosa Offriamo</p>
            <h2 className="font-serif text-4xl md:text-5xl text-text-main tracking-tight">I Nostri Servizi</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service, index) => (
              <div key={index} className={`bg-white p-8 rounded-xl transition-all duration-500 hover:shadow-xl hover:-translate-y-1 ${servicesInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`} style={{ transitionDelay: `${index * 100}ms` }}>
                <div className="w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center mb-6">
                  <service.icon size={28} className="text-primary" />
                </div>
                <h3 className="font-serif text-xl text-text-main mb-3">{service.title}</h3>
                <p className="font-sans text-sm text-text-muted">{service.desc}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-12">
            <Link to="/services" className="inline-flex items-center bg-primary hover:bg-primary-hover text-white px-8 py-4 rounded-md font-sans font-medium transition-all btn-primary">
              Scopri di più <ArrowRight size={18} className="ml-2" />
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section ref={whyRef} className="py-24 bg-background" data-testid="why-us-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className={`transition-all duration-700 ${whyInView ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'}`}>
              <p className="text-xs font-sans uppercase tracking-[0.2em] font-medium text-primary mb-4">Perché Sceglierci</p>
              <h2 className="font-serif text-4xl md:text-5xl text-text-main tracking-tight mb-6">La tua fiducia è il nostro successo</h2>
              <p className="font-sans text-text-muted leading-relaxed mb-8">
                Dal 2011 siamo al fianco dei nostri clienti, offrendo un servizio personalizzato e professionale. La nostra esperienza nel mercato immobiliare di Caserta ci permette di guidarti in ogni fase della compravendita.
              </p>
              <ul className="space-y-4">
                {['Consulenza personalizzata', 'Valutazioni accurate', 'Assistenza legale', 'Supporto post-vendita'].map((item, i) => (
                  <li key={i} className="flex items-center font-sans text-text-main">
                    <CheckCircle size={20} className="text-primary mr-3" /> {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className={`relative transition-all duration-700 ${whyInView ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'}`}>
              <img src="https://customer-assets.emergentagent.com/job_green-properties-crt/artifacts/631gtud1_immiagine%20sito%201.jpg" alt="Ufficio Green House - servizi immobiliari Caserta" className="rounded-xl shadow-xl" />
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-primary" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-serif text-4xl md:text-5xl text-white tracking-tight mb-6">Vuoi vendere il tuo immobile?</h2>
          <p className="font-sans text-base md:text-lg text-white/80 mb-8">Contattaci per una valutazione gratuita e senza impegno.</p>
          <Link to="/sell" className="inline-flex items-center bg-white text-primary hover:bg-white/90 px-8 py-4 rounded-md font-sans font-medium transition-all" data-testid="cta-sell-final">
            Richiedi Valutazione <ArrowRight size={18} className="ml-2" />
          </Link>
        </div>
      </section>
    </div>
  );
}
